import { useQuery } from '@tanstack/react-query';

import {
  getEmployeeGoal,
  getEmployeeProfile,
  getEmployeeRating,
} from '../../../shared/api/employee.service';
import { useEmployeeSession } from '../../../shared/hooks/use-employee-session';
import {
  resolveEmployeeProfileName,
  resolveEmployeeShowRating,
  resolveRatingValueLabel,
} from '../../../shared/model';
import { ProfileRating, ProfileSummaryCard } from '../../../shared/ui';
import { useRegistrationCompletePreviewContent } from '../model';

import { RegistrationCompleteProfileAvatar } from './registration-complete-profile-avatar';

type RegistrationCompletePreviewVariant = 'with-photo' | 'without-photo';
type RegistrationCompleteProfileSummaryCardVariant = 'compact' | 'preview';

type RegistrationCompleteProfileSummaryCardProps = {
  cardVariant?: RegistrationCompleteProfileSummaryCardVariant;
  previewVariant: RegistrationCompletePreviewVariant;
};

export function RegistrationCompleteProfileSummaryCard({
  cardVariant = 'compact',
  previewVariant,
}: RegistrationCompleteProfileSummaryCardProps) {
  const { authContact, status } = useEmployeeSession();
  const profileQuery = useQuery({
    enabled: status === 'authenticated',
    queryFn: getEmployeeProfile,
    queryKey: ['employee', 'profile'],
  });
  const goalQuery = useQuery({
    enabled: status === 'authenticated',
    queryFn: getEmployeeGoal,
    queryKey: ['employee', 'goal'],
  });
  const ratingQuery = useQuery({
    enabled: status === 'authenticated',
    queryFn: getEmployeeRating,
    queryKey: ['employee', 'rating'],
  });
  const profile = profileQuery.data ?? null;
  const { goalText, profileNameFallback, progressLabel, progressPercent } =
    useRegistrationCompletePreviewContent(
      {
        authContact,
        goal: goalQuery.data ?? null,
        profile,
      },
      previewVariant,
    );
  const profileName = resolveEmployeeProfileName(profile, profileNameFallback);
  const isRatingVisible = resolveEmployeeShowRating(profile);
  const ratingValueLabel = resolveRatingValueLabel(ratingQuery.data);
  const avatarSizePx = cardVariant === 'preview' ? 64 : 49;
  const ratingProps =
    cardVariant === 'preview'
      ? {
          gapPx: 3,
          starGapPx: 2,
          starSizePx: 11,
          valueFontSizePx: 13,
          valueFontWeight: 600,
          valueTone: 'default' as const,
        }
      : {
          gapPx: 1,
          starGapPx: 1,
          starSizePx: 7,
          valueFontSizePx: 7,
          valueFontWeight: 400,
          valueTone: 'emphasis' as const,
        };

  return (
    <ProfileSummaryCard
      avatar={
        <RegistrationCompleteProfileAvatar
          avatarUrl={profile?.avatarUrl ?? null}
          previewVariant={previewVariant}
          sizePx={avatarSizePx}
        />
      }
      avatarSlotWidthPx={avatarSizePx}
      dataUi="registration-complete-profile-summary-card"
      goalText={goalText}
      profileName={profileName}
      progressLabel={progressLabel}
      progressPercent={progressPercent}
      progressTrackTone="neutral"
      ratingSlot={
        isRatingVisible ? (
          <ProfileRating
            gapPx={ratingProps.gapPx}
            starGapPx={ratingProps.starGapPx}
            starSizePx={ratingProps.starSizePx}
            valueFontSizePx={ratingProps.valueFontSizePx}
            valueFontWeight={ratingProps.valueFontWeight}
            valueLabel={ratingValueLabel}
            valueTone={ratingProps.valueTone}
          />
        ) : null
      }
      variant={cardVariant}
    />
  );
}
